import { Injectable, signal } from '@angular/core';

export type NotificationKind = 'success' | 'error';

export interface AppNotification {
  id: number;
  kind: NotificationKind;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class HttpNotificationService {
  readonly notifications = signal<AppNotification[]>([]);
  private nextId = 0;
  private readonly hideTimers = new Map<number, ReturnType<typeof setTimeout>>();

  show(kind: NotificationKind, message: string, durationMs: number = 6000): void {
    const existing = this.notifications().find(n => n.kind === kind && n.message === message);
    if (existing) {
      this.startHideTimer(existing.id, durationMs);
      return;
    }

    const id = ++this.nextId;
    this.notifications.update(list => [...list, { id, kind, message }]);
    this.startHideTimer(id, durationMs);
  }

  private startHideTimer(id: number, durationMs: number): void {
    const existingTimer = this.hideTimers.get(id);
    if (existingTimer !== undefined) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.dismiss(id);
    }, durationMs);

    this.hideTimers.set(id, timer);
  }

  dismiss(id: number): void {
    this.notifications.update(list => list.filter(n => n.id !== id));
    const timer = this.hideTimers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.hideTimers.delete(id);
    }
  }

  showSuccess(message: string, durationMs?: number): void {
    this.show('success', message, durationMs);
  }

  showError(message: string, durationMs?: number): void {
    this.show('error', message, durationMs);
  }
}
